import { useEffect, useState } from 'react'
import { Modal, Spinner, Table, Badge, Button } from 'react-bootstrap'
import { IStandaloneBillings } from '../../../domain/usecases/StandaloneBillings'
import { getRecipientAccountLabel } from '../../../config/recipient-accounts'
import {
  PaymentMethod,
  StandaloneBilling,
  StandaloneBillingStatus
} from '../../../domain/models/StandaloneBillingModel'
import { formatDateOnly } from '../formatDateOnly'

export interface StandaloneBillingDetailsDrawerProps {
  billingId: string | null
  service: IStandaloneBillings
  onClose: () => void
}

const formatCurrency = (n: number | string | null | undefined) => {
  if (n === null || n === undefined) return '-'
  const v = typeof n === 'string' ? parseFloat(n) : n
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const formatDateTime = (s: string | null | undefined) => {
  if (!s) return '-'
  return new Date(s).toLocaleString('pt-BR')
}

const methodLabel = (m: PaymentMethod) => {
  switch (m) {
    case PaymentMethod.ITAU_BOLETO:
      return 'Boleto Itaú'
    case PaymentMethod.ITAU_BOLEPIX:
      return 'Bolepix Itaú'
    case PaymentMethod.TED:
      return 'TED'
    default:
      return m
  }
}

const statusLabel = (s: StandaloneBillingStatus) => {
  switch (s) {
    case StandaloneBillingStatus.PAID:
      return 'Paga'
    case StandaloneBillingStatus.CANCELLED:
      return 'Cancelada'
    default:
      return 'Pendente'
  }
}

const statusVariant = (s: StandaloneBillingStatus) => {
  switch (s) {
    case StandaloneBillingStatus.PAID:
      return 'success'
    case StandaloneBillingStatus.CANCELLED:
      return 'secondary'
    default:
      return 'warning'
  }
}

export const StandaloneBillingDetailsDrawer: React.FC<
  StandaloneBillingDetailsDrawerProps
> = ({ billingId, service, onClose }) => {
  const [billing, setBilling] = useState<StandaloneBilling | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!billingId) {
      setBilling(null)
      return
    }
    setLoading(true)
    service
      .getOne({ id: billingId })
      .then((b) => setBilling(b))
      .catch((e) => {
        console.error(e)
        setBilling(null)
      })
      .finally(() => setLoading(false))
  }, [billingId, service])

  return (
    <Modal show={!!billingId} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Detalhes da Cobrança Avulsa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        )}
        {!loading && billing && (
          <>
            <h6>Cobrança</h6>
            <Table size="sm" borderless>
              <tbody>
                <tr>
                  <th>Status</th>
                  <td>
                    <Badge variant={statusVariant(billing.status)}>
                      {statusLabel(billing.status)}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <th>Valor</th>
                  <td>{formatCurrency(billing.amount)}</td>
                </tr>
                <tr>
                  <th>Vencimento</th>
                  <td>{formatDateOnly(billing.due_date)}</td>
                </tr>
                <tr>
                  <th>Descrição</th>
                  <td>{billing.description}</td>
                </tr>
                <tr>
                  <th>Método</th>
                  <td>{methodLabel(billing.payment_method)}</td>
                </tr>
                <tr>
                  <th>Conta beneficiária</th>
                  <td>
                    {getRecipientAccountLabel(billing.recipient_account_key)}
                  </td>
                </tr>
                <tr>
                  <th>Criada em</th>
                  <td>{formatDateTime(billing.created_at)}</td>
                </tr>
              </tbody>
            </Table>

            <h6 className="mt-3">Pagador</h6>
            <Table size="sm" borderless>
              <tbody>
                <tr>
                  <th>Nome</th>
                  <td>
                    {billing.payer_person?.info?.name ||
                      billing.payer_person?.info?.business_name ||
                      '-'}
                  </td>
                </tr>
                <tr>
                  <th>Documento</th>
                  <td>
                    {billing.payer_person?.info?.cpf ||
                      billing.payer_person?.info?.cnpj ||
                      '-'}
                  </td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{billing.payer_person?.contact_info?.email || '-'}</td>
                </tr>
                <tr>
                  <th>Telefone</th>
                  <td>
                    {billing.payer_person?.contact_info?.mobile_phone || '-'}
                  </td>
                </tr>
              </tbody>
            </Table>

            {(billing.boleto_url ||
              billing.boleto_barcode ||
              billing.itau_txid) && (
              <>
                <h6 className="mt-3">Boleto / Bolepix</h6>
                <Table size="sm" borderless>
                  <tbody>
                    {billing.boleto_url && (
                      <tr>
                        <th>URL</th>
                        <td>
                          <a
                            href={billing.boleto_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Abrir
                          </a>
                        </td>
                      </tr>
                    )}
                    {billing.boleto_barcode && (
                      <tr>
                        <th>Código de barras</th>
                        <td>
                          <code>{billing.boleto_barcode}</code>
                        </td>
                      </tr>
                    )}
                    {billing.boleto_our_number && (
                      <tr>
                        <th>Nosso número</th>
                        <td>{billing.boleto_our_number}</td>
                      </tr>
                    )}
                    {billing.itau_txid && (
                      <tr>
                        <th>txid (Pix)</th>
                        <td>
                          <code>{billing.itau_txid}</code>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {billing.status === StandaloneBillingStatus.PAID && (
              <>
                <h6 className="mt-3">Pagamento</h6>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <th>Pago em</th>
                      <td>{formatDateTime(billing.paid_at)}</td>
                    </tr>
                    <tr>
                      <th>Valor pago</th>
                      <td>{formatCurrency(billing.paid_amount)}</td>
                    </tr>
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
